import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'
import OnboardingLinks from './OnboardingLinks'
import TransferAlertBanner from './TransferAlertBanner'
import CaseOutcomeReportSection from './CaseOutcomeReportSection'

/**
 * CaseHeader — port of Scale's CaseHeader. Top banner of the case detail
 * page exactly matching Scale Portal's mediator view:
 *
 *   Row 1: Case Title + (Mediation Date / Trial Date)         | Booking Date
 *   Row 2: Plaintiff traffic-light row | Defendant row | 4 action buttons
 *
 * Each traffic-light row shows three labeled dots: Onboarding / Payment /
 * Mediation Statement, with the color driven by per-party progress.
 */

// Hex values are kept identical to Cases.jsx STATUS_COLOR_MAP so the same
// case renders the exact same dot colors on both pages. Note: 'grey' on
// the API ('unknown / not started yet') is treated as amber, matching the
// Cases-tab + Dashboard convention — never gray.
const COLOR_HEX = {
    green: '#22c55e',
    yellow: '#f59e0b',
    red: '#ef4444',
    grey: '#f59e0b',
}

function Dot({ color }) {
    const hex = COLOR_HEX[color] || COLOR_HEX.red
    return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: hex }} />
}

function fmtDate(s) {
    if (!s) return 'N/A'
    const d = new Date(s.length === 10 ? s + 'T00:00:00' : s)
    if (isNaN(d.getTime())) return 'N/A'
    // Scale's screenshot format: "May 04, 2026" — full month, zero-padded day.
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })
}

// Booking date must render in the MEDIATOR's timezone (matching Scale Portal,
// which does momentTimezone(date).tz(mediator.timezone)). Using the browser's
// local timezone shifts a late-UTC created_at across the date boundary — e.g.
// 2026-04-15T23:18Z shows Apr 16 in UTC+5 but Apr 15 in US/Pacific.
function fmtDateTz(raw, tz) {
    if (!raw) return 'N/A'
    // Pure calendar date (YYYY-MM-DD): no time/zone, render as-is in UTC.
    if (typeof raw === 'string' && raw.length === 10) {
        const d = new Date(raw + 'T00:00:00Z')
        return isNaN(d.getTime())
            ? 'N/A'
            : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit', timeZone: 'UTC' })
    }
    const d = new Date(raw)
    if (isNaN(d.getTime())) return 'N/A'
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        timeZone: tz || 'America/Los_Angeles',
    })
}

function fmtSlot(slot) {
    if (!slot) return ''
    return ({ morning: 'Morning', afternoon: 'Afternoon', fullday: 'Full Day' }[slot]) || slot
}

function PartyTrafficRow({ party, label, briefLabel = 'Brief' }) {
    // Always render the row so the Plaintiff / Defendant labels never
    // disappear from the header. When party data isn't available yet
    // (still loading or no client linked), we render grey dots instead.
    const safe = party || {}
    return (
        <div className="flex-1">
            <p className="text-xs font-medium mb-2 text-gray-500">{label}</p>
            <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-1.5">
                    <Dot color={safe.onboardingColor || 'grey'} />
                    <span className="text-xs text-gray-600 capitalize">Onboarding</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Dot color={safe.paymentColor || 'grey'} />
                    <span className="text-xs text-gray-600 capitalize">Payment</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Dot color={safe.briefColor || 'grey'} />
                    <span className="text-xs text-gray-600 capitalize">{briefLabel}</span>
                </div>
            </div>
        </div>
    )
}

export default function CaseHeader({ onToast, backLabel = 'Cases', backTo = '/cases', onCaseRefresh }) {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel, is_gsc_case } = useContext(CaseDetailContext)
    const navigate = useNavigate()
    const [carActionLoading, setCarActionLoading] = useState(null) // 'approve' | 'reject' | 'hold' | null

    /* Inline rename, matching the pencil beside the title in Scale Portal
     * (CaseInformation.jsx). Case managers were sending us names to correct by
     * hand — e.g. "Gonzales v. J &amp; D Meat Company", where an escaped
     * ampersand was saved at booking. PUT /cases/:id already exists and is
     * scope-guarded to the case manager's own mediators. */
    const [isEditingName, setIsEditingName] = useState(false)
    const [nameDraft, setNameDraft] = useState('')
    const [isSavingName, setIsSavingName] = useState(false)

    const startEditingName = () => {
        setNameDraft(caseData?.caseName || '')
        setIsEditingName(true)
    }

    const saveCaseName = async () => {
        const trimmed = nameDraft.trim()
        if (!trimmed) {
            onToast?.({ type: 'error', message: 'Case name cannot be empty' })
            return
        }
        // Unchanged: close without a round trip.
        if (trimmed === (caseData?.caseName || '').trim()) {
            setIsEditingName(false)
            return
        }
        setIsSavingName(true)
        try {
            await api.put(`/cases/${caseData.id}`, { case_name: trimmed })
            // Refetch rather than patching locally: the name is read in several
            // places on this page and they should all agree.
            if (onCaseRefresh) await onCaseRefresh()
            onToast?.({ type: 'success', message: 'Case name updated' })
            setIsEditingName(false)
        } catch (err) {
            onToast?.({
                type: 'error',
                message: err?.response?.data?.message || 'Could not update the case name',
            })
        } finally {
            setIsSavingName(false)
        }
    }

    // Submit a CAR-case decision on behalf of the assigned mediator. Mirrors
    // Scale Portal's `submitMediatorDecision` flow — case-manager proxies the
    // call to its own BE which forwards to scale-backend with the assigned
    // mediator's external id.
    const submitCarDecision = async (decision) => {
        if (!caseData?.id) return
        try {
            setCarActionLoading(decision)
            const res = await api.post(`/cases/${caseData.id}/car-decision`, {
                decision, // 'approve' | 'reject' | 'hold'
            })
            const message = res?.data?.message
                || (decision === 'approve' ? 'Case approved' : decision === 'reject' ? 'Case rejected' : 'Case placed on hold')
            onToast?.({ type: 'success', message })
            if (onCaseRefresh) await onCaseRefresh()
        } catch (err) {
            const errMsg = err?.response?.data?.message || `Failed to ${decision} case`
            onToast?.({ type: 'error', message: errMsg })
        } finally {
            setCarActionLoading(null)
        }
    }

    if (!caseData) return null

    const transferHistory = caseData.transferHistory || caseData.case_transfer_history?.[0] || null
    const isTransferToCurrent = !!transferHistory && (
        !caseData.mediator?.mediatorId ||
        transferHistory.to_mediator?.mediator_id === caseData.mediator.mediatorId
    )

    const mediatorName = caseData.mediatorName || ''
    const trialDate = caseData.trialDate || caseData.casemetadata?.trial_date
    // Scale defaults to "Brief"; mediator-specific brief_text overrides.
    const briefLabel = caseData.mediatorSettings?.brief_text || 'Brief'

    return (
        <div className="w-full pb-2">
            <button
                type="button"
                onClick={() => navigate(backTo)}
                className="text-sm font-semibold text-[#007C7A] cursor-pointer hover:underline mb-2"
            >
                {`← Back to ${mediatorName ? `${mediatorName} Profile` : backLabel}`}
            </button>

            <CaseOutcomeReportSection onToast={onToast} />

            {isTransferToCurrent && (
                <TransferAlertBanner
                    transferHistory={transferHistory}
                    caseData={caseData}
                    mediatorData={caseData.mediator}
                />
            )}

            {/* CAR-case Approve / Reject / Hold buttons (only on CAR cases). Wired
                to the BE proxy at /cases/:id/car-decision which forwards to
                scale-backend's submitMediatorDecision endpoint. */}
            {(caseData.is_car_case || caseData.is_car_case_not_scheduled) && (
                <div className="mb-3 flex items-center gap-2">
                    <button
                        type="button"
                        disabled={!!carActionLoading}
                        onClick={() => submitCarDecision('approve')}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-60"
                        title="You need at least 20 available dates to approve a case"
                    >
                        {carActionLoading === 'approve' ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                        type="button"
                        disabled={!!carActionLoading}
                        onClick={() => {
                            if (window.confirm('Reject this case? This will clear the mediator assignment.')) {
                                submitCarDecision('reject')
                            }
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-60"
                    >
                        {carActionLoading === 'reject' ? 'Rejecting…' : 'Reject'}
                    </button>
                    <button
                        type="button"
                        disabled={!!carActionLoading}
                        onClick={() => {
                            if (window.confirm('Place this case on hold (take it off the calendar)?')) {
                                submitCarDecision('hold')
                            }
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-md disabled:opacity-60"
                    >
                        {carActionLoading === 'hold' ? 'Holding…' : 'Hold'}
                    </button>
                </div>
            )}

            {/* CRM Submission Report (placeholder — Scale's CRM integration
                is mediator-side; we expose the visible button for parity) */}
            {caseData.casemetadata?.crm_status && (
                <div className="mb-3 px-4 py-3 rounded-md bg-purple-50 border border-purple-200 text-sm text-purple-900 flex items-center justify-between">
                    <div>
                        <span className="font-semibold">CRM Status:</span> {caseData.casemetadata.crm_status}
                    </div>
                    <button
                        type="button"
                        onClick={() => onToast?.({ type: 'warning', message: 'CRM submission is handled on Scale Portal' })}
                        className="text-xs text-purple-800 hover:underline"
                    >
                        View report →
                    </button>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 md:p-6">
                    {/* Row 1: Title + dates row | Booking date right-aligned */}
                    <div className="flex flex-col md:flex-row justify-between items-start border-b mb-2 pb-3">
                        <div className="mb-3 md:mb-0 flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-2 md:mb-1">
                                {isEditingName ? (
                                    /* Capped at 75% of the title column: at full
                                       width the Cancel button ran under the
                                       booking date sitting to the right. */
                                    <div className="flex items-center gap-2 w-full md:w-3/4 min-w-0">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={nameDraft}
                                            onChange={(e) => setNameDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveCaseName()
                                                if (e.key === 'Escape') setIsEditingName(false)
                                            }}
                                            disabled={isSavingName}
                                            className="flex-1 min-w-0 text-sm sm:text-base md:text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-600"
                                        />
                                        <button
                                            type="button"
                                            onClick={saveCaseName}
                                            disabled={isSavingName}
                                            className="shrink-0 text-xs font-medium px-2 py-1 rounded bg-teal-700 text-white disabled:opacity-60"
                                        >
                                            {isSavingName ? 'Saving…' : 'Save'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsEditingName(false)}
                                            disabled={isSavingName}
                                            className="shrink-0 text-xs font-medium px-2 py-1 rounded border border-gray-300 text-gray-700 disabled:opacity-60"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <h4 className="text-sm sm:text-base md:text-lg font-semibold break-words pr-2 flex items-center gap-2">
                                        <span>
                                            {caseData.rawStatus === 'cancelled' && 'CANCELED - '}
                                            {caseData.coupon?.coupon_code && `${caseData.coupon.coupon_code} - `}
                                            {caseData.caseName || '(No case name)'}
                                            {caseData.caseNumber ? ` (${caseData.caseNumber})` : ''}
                                        </span>
                                        {/* Hidden on a cancelled case: renaming one is never the
                                            intent, and Scale prefixes "CANCELED - " here too. */}
                                        {caseData.rawStatus !== 'cancelled' && (
                                            <button
                                                type="button"
                                                onClick={startEditingName}
                                                title="Edit case name"
                                                aria-label="Edit case name"
                                                className="shrink-0 text-gray-400 hover:text-teal-700 transition-colors"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 20h9" />
                                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                                </svg>
                                            </button>
                                        )}
                                    </h4>
                                )}
                            </div>
                            <div className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-3">
                                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                        Mediation Date:{' '}
                                        <span className="text-sm font-semibold text-[#111827]">
                                            {fmtDate(caseData.date)}
                                        </span>
                                    </p>
                                    {caseData.slotType && (
                                        <span className="font-semibold bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-md">
                                            {fmtSlot(caseData.slotType)}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 whitespace-nowrap">
                                    Trial Date:{' '}
                                    <span className="text-sm font-semibold text-[#111827]">
                                        {fmtDate(trialDate)}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-row items-start md:items-start">
                            <p className="text-xs text-gray-500 whitespace-nowrap">
                                Booking Date:{' '}
                                <span className="text-sm font-semibold text-[#111827]">
                                    {fmtDateTz(
                                        caseData.reschedule_date || caseData.createdAt,
                                        caseData.mediator?.timezone
                                    )}
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Row 2: Two-party traffic lights | Action buttons */}
                    <div className="w-full mt-4 flex flex-col xl:flex-row xl:justify-between xl:items-start gap-4 xl:gap-6">
                        <div className="w-full xl:w-[40%]">
                            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                                <PartyTrafficRow party={caseData.plaintiff} label={plaintiffPartyLabel} briefLabel={briefLabel} />
                                {/* Scale parity (CaseTrafficLights.jsx:71): GSC cases
                                    have a single firm party, so the defendant
                                    traffic-light row is hidden. */}
                                {!is_gsc_case && (
                                    <PartyTrafficRow party={caseData.defendant} label={defendantPartyLabel} briefLabel={briefLabel} />
                                )}
                            </div>
                        </div>
                        <div className="w-full xl:w-auto xl:flex-shrink-0">
                            <OnboardingLinks onToast={onToast} compact />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
